import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './SpecialOffers.css';

const SpecialOffers = ({ promotions }) => {
  if (!promotions || promotions.length === 0) {
    return null;
  }

  // Filter out the featured promotion (shown in banner)
  const regularPromotions = promotions.filter(promo => !promo.featured);

  if (regularPromotions.length === 0) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const getPromotionTypeIcon = (type) => {
    switch (type) {
      case 'discount':
        return 'fa-percent';
      case 'package':
        return 'fa-box-open';
      case 'seasonal':
        return 'fa-calendar-alt';
      case 'limited':
        return 'fa-clock';
      case 'new_customer':
        return 'fa-star';
      default:
        return 'fa-tag';
    }
  };

  return (
    <section className="special-offers">
      <div className="special-offers__container">
        <motion.div
          className="special-offers__header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="special-offers__title">
            <i className="fas fa-fire"></i>
            Current Special Offers
          </h2>
          <p className="special-offers__subtitle">
            Limited time deals you don't want to miss
          </p>
        </motion.div>

        <motion.div
          className="special-offers__grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {regularPromotions.map((promotion) => (
            <motion.div
              key={promotion.id}
              className="offer-card"
              variants={cardVariants}
            >
              <div className="offer-card__image-wrapper">
                <img
                  src={promotion.image}
                  alt={promotion.title}
                  className="offer-card__image"
                  loading="lazy"
                />
                {promotion.badge_text && (
                  <div className="offer-card__badge">
                    {promotion.badge_text}
                  </div>
                )}
                <div className="offer-card__type-badge">
                  <i className={`fas ${getPromotionTypeIcon(promotion.promotion_type)}`}></i>
                </div>
              </div>

              <div className="offer-card__content">
                <h3 className="offer-card__title">{promotion.title}</h3>

                {promotion.subtitle && (
                  <p className="offer-card__subtitle">{promotion.subtitle}</p>
                )}

                <div className="offer-card__pricing">
                  {promotion.original_price && (
                    <span className="offer-card__original-price">
                      ${parseFloat(promotion.original_price).toFixed(2)}
                    </span>
                  )}
                  <span className="offer-card__promotional-price">
                    ${parseFloat(promotion.promotional_price).toFixed(2)}
                  </span>
                  {promotion.savings_percentage > 0 && (
                    <span className="offer-card__savings">
                      Save {promotion.savings_percentage}%
                    </span>
                  )}
                </div>

                <div
                  className="offer-card__description"
                  dangerouslySetInnerHTML={{ __html: promotion.description }}
                />

                {promotion.end_date && (
                  <div className="offer-card__expires">
                    <i className="fas fa-clock"></i>
                    Expires: {new Date(promotion.end_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                )}

                <Link
                  to={promotion.cta_link || '/booking'}
                  className="offer-card__cta"
                >
                  {promotion.cta_text || 'Book Now'}
                  <i className="fas fa-arrow-right"></i>
                </Link>

                {promotion.terms_conditions && (
                  <button
                    className="offer-card__terms-toggle"
                    onClick={(e) => {
                      e.currentTarget.nextElementSibling.classList.toggle('show');
                    }}
                  >
                    <i className="fas fa-info-circle"></i>
                    Terms & Conditions
                  </button>
                )}

                {promotion.terms_conditions && (
                  <div className="offer-card__terms">
                    <p>{promotion.terms_conditions}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SpecialOffers;
